import React, { FC, useState, useEffect } from 'react';
import { useSelector, shallowEqual } from 'react-redux';
import { FormattedMessage } from 'react-intl';
import {
  WatcherSearchQueryParams,
  WatcherSearchQuery,
  WatcherSearchQueryEnum,
  WatcherQueryParamsEnum,
} from '../../../watchers/watchers.types';
import {
  ModalScrollableContent,
  ModalScrollableFooter,
  ModalScrollableHeader,
  ModalScrollableContainer,
  ModalScrollableBody,
  ModalActions,
} from '../../../components/modal/index';
import styles from './add_decision_watcher_modal.module.scss';
import { TextButton, PrimaryButton } from '../../../components/button';
import { FilterItem } from '../search-snippets/aside/filter-box/FilterItem';
import { AddWatcherCommentaries } from '../../../watchers/components/add-watcher-commentaries/AddWatcherCommentaries';
import { isCommentarySearchQuery } from '../../../watchers/WatchersUtils';
import { StateType } from '../../../state/reducers';
import { formatQueryWatcherModal } from './searchWatcherUtils';

type Props = {
  closeModal: () => void;
  isOpen: boolean;
  queryParameters: WatcherSearchQueryParams;
  tags: string[];
  createWatcher: (searchQueries: Array<WatcherSearchQuery>) => void;
};

export const AddDecisionWatcherModal: FC<Props> = ({
  closeModal = () => {},
  isOpen = false,
  queryParameters,
  tags,
  createWatcher,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [hasError, setHasError] = useState(false);
  const [isChecked, setIsChecked] = useState(false);

  const { isCommentaryQueryCreated } = useSelector(
    ({ watcherExists }: StateType) => ({
      isCommentaryQueryCreated:
        !!watcherExists.watcherKey &&
        watcherExists.types.includes(WatcherSearchQueryEnum.COMMENTARY),
    }),
    shallowEqual,
  );

  useEffect(() => {
    setIsChecked(isCommentaryQueryCreated);
  }, [isCommentaryQueryCreated]);

  const [searchQueries, setSearchQueries] = useState<Array<WatcherSearchQuery>>(
    [
      {
        type: WatcherSearchQueryEnum.DECISION,
        query: {
          ...queryParameters,
          type: WatcherQueryParamsEnum.DECISION,
        },
      },
    ],
  );

  const addCommentaryQueryType = () => {
    setIsChecked(true);
    setSearchQueries(
      searchQueries.concat([
        {
          type: WatcherSearchQueryEnum.COMMENTARY,
          query: {
            type: WatcherQueryParamsEnum.COMMENTARY,
            query: queryParameters.query,
          },
        },
      ]),
    );
  };

  const removeCommentaryQueryType = () => {
    setIsChecked(false);
    setSearchQueries(
      searchQueries.filter(
        searchQuery => !isCommentarySearchQuery(searchQuery),
      ),
    );
  };

  const validateWatcherCreation = async (
    event: React.MouseEvent<HTMLButtonElement>,
  ) => {
    event.preventDefault();
    setIsLoading(true);
    try {
      const watcherQueriesToCreate = isCommentaryQueryCreated
        ? searchQueries.filter(
            searchQuery => !isCommentarySearchQuery(searchQuery),
          )
        : searchQueries;
      await createWatcher(watcherQueriesToCreate);
      setIsLoading(false);
      closeModal();
    } catch (error) {
      setIsLoading(false);
      setHasError(true);
      throw error;
    }
  };

  return (
    <ModalScrollableContainer isOpen={isOpen} onRequestClose={closeModal}>
      <ModalScrollableHeader
        title={
          <FormattedMessage id="search/components/search-watcher/AddDecisionWatcherModal.tsx/4" />
        }
        onClose={closeModal}
      />
      <ModalScrollableContent>
        <ModalScrollableBody>
          <div className={styles.content}>
            <div className={styles.subtitle}>
              <FormattedMessage id="search/components/search-watcher/AddDecisionWatcherModal.tsx/5" />
            </div>

            <div className={styles.query}>
              {formatQueryWatcherModal(queryParameters)}
            </div>
            {tags.length > 0 && (
              <ul className={styles.tags}>
                {tags.map((tag, tagIndex) => (
                  /* eslint-disable-next-line react/no-array-index-key */
                  <li key={`tag-${tagIndex}`}>
                    <FilterItem filter={{ label: tag }} />
                  </li>
                ))}
              </ul>
            )}
            <div className={styles.commentaries}>
              <AddWatcherCommentaries
                addCommentaryQueryType={addCommentaryQueryType}
                removeCommentaryQueryType={removeCommentaryQueryType}
                isChecked={isChecked}
              />
            </div>
            {hasError && (
              <div className={styles.error}>
                <FormattedMessage id="search/components/search-watcher/AddDecisionWatcherModal.tsx/1" />
              </div>
            )}
          </div>
        </ModalScrollableBody>
      </ModalScrollableContent>
      <ModalScrollableFooter>
        <ModalActions>
          <div className={styles.button}>
            <TextButton onClick={closeModal}>
              <FormattedMessage id="search/components/search-watcher/AddDecisionWatcherModal.tsx/2" />
            </TextButton>
          </div>
          <PrimaryButton
            disabled={isLoading}
            dataName="confirm-add-watcher"
            onClick={validateWatcherCreation}
          >
            <FormattedMessage id="search/components/search-watcher/AddDecisionWatcherModal.tsx/3" />
          </PrimaryButton>
        </ModalActions>
      </ModalScrollableFooter>
    </ModalScrollableContainer>
  );
};
